import assert from 'minimalistic-assert';
import Address from '../address/index.js';
import AddressSet from '../address-set/index.js';
import { isNumberUnit } from '@exodus/currency';
import lodash from 'lodash';
import { ModelIdSymbol } from '../constants.js';
import { createIsInstance } from '../utils.js';
const { differenceBy, groupBy, isEqual, sortBy, uniq, uniqBy } = lodash;
function pickCurrencyFromArray(utxos) {
    if (!Array.isArray(utxos))
        return;
    for (const utxo of utxos) {
        if (typeof utxo.value === 'object' && utxo.value.unitType)
            return utxo.value.unitType;
    }
}
export default class UtxoCollection {
    currency;
    #data = null;
    #addresses;
    #value;
    #size;
    constructor({ currency } = Object.create(null)) {
        this.currency = currency;
    }
    static get [ModelIdSymbol]() {
        return 'UtxoCollection';
    }
    static _isInstance = createIsInstance(UtxoCollection);
    static [Symbol.hasInstance](x) {
        return this._isInstance(x);
    }
    static isInstance = UtxoCollection[Symbol.hasInstance];
    static createEmpty(options = Object.create(null)) {
        return UtxoCollection.fromArray([], options);
    }
    static fromArray(utxoArray, { addressMap, ...options } = Object.create(null)) {
        const currency = options.currency || pickCurrencyFromArray(utxoArray);
        if (currency == null && utxoArray == null) {
            console.warn('UtxoCollection.fromArray(): both currency and utxoArray are null.');
        }
        else if (currency == null && utxoArray?.length === 0) {
            console.warn('UtxoCollection.fromArray(): currency is null and utxoArray has zero items.');
        }
        utxoArray = uniqBy(utxoArray, (utxo) => `${utxo.txId}:${utxo.vout}`);
        const utxos = utxoArray.map((utxo) => {
            assert(utxo.address, 'utxo.address must be provided');
            assert(utxo.address instanceof Address || addressMap, 'Must pass addressMap if utxo.address is not of type Address');
            utxo.address = utxo.address instanceof Address ? utxo.address : addressMap[utxo.address];
            return {
                ...utxo,
                value: isNumberUnit(utxo.value) ? utxo.value : currency.parse(utxo.value),
            };
        });
        const col = new UtxoCollection({ currency });
        col.#data = Object.freeze(groupBy(utxos, (utxo) => utxo.address.toString()));
        return col;
    }
    static fromJSON(json, { currency }) {
        const data = typeof json === 'string' ? JSON.parse(json) : json;
        const utxoData = Object.create(null);
        Object.keys(data).forEach((address) => {
            const addr = new Address(data[address].address, { path: data[address].path });
            const sortedUtxos = sortBy(data[address].utxos, (utxo) => currency.parse(utxo.value).toDefaultNumber());
            utxoData[address] = sortedUtxos.map((utxo) => ({
                ...utxo,
                address: addr,
                value: currency.parse(utxo.value),
            }));
        });
        const col = new UtxoCollection({ currency });
        col.#data = Object.freeze(utxoData);
        return col;
    }
    clone() {
        return UtxoCollection.fromArray(this.toArray(), { ...this });
    }
    equals(utxoCol) {
        return isEqual(this.toJSON(), utxoCol.toJSON());
    }
    difference(utxoCol) {
        const txMap = (col) => {
            const map = Object.create(null);
            col.toArray().forEach((utxo) => {
                const key = `${utxo.txId}/${utxo.vout}`;
                map[key] = utxo;
            });
            return map;
        };
        const thisMap = txMap(this);
        const otherMap = txMap(utxoCol);
        const retUtxos = [];
        Object.keys(thisMap).forEach((key) => {
            if (!otherMap[key])
                retUtxos.push(thisMap[key]);
        });
        return UtxoCollection.fromArray(retUtxos, { ...this });
    }
    getAddressUtxos(address) {
        assert(address.toString() in this.#data, `${address} is not present in this UTXO collection`);
        return UtxoCollection.fromArray(this.#data[address.toString()], { ...this });
    }
    getTxIdUtxos(txId) {
        const arrUtxos = this.toArray().filter((utxo) => utxo.txId === txId);
        return UtxoCollection.fromArray(arrUtxos, { ...this });
    }
    getAddressesForTxId(txId) {
        const arrUtxos = this.toArray().filter((utxo) => utxo.txId === txId);
        const arrAddrs = arrUtxos.map((utxo) => utxo.address);
        return AddressSet.fromArray(arrAddrs);
    }
    getAddressPathsMap() {
        return [...this.addresses].reduce((obj, addr) => {
            return Object.assign(obj, { [addr.toString()]: addr.meta.path });
        }, Object.create(null));
    }
    hasAddressUtxos(address) {
        return String(address) in this.#data;
    }
    inspect() {
        return `<UtxoCollection size: ${this.size}, value: ${this.value.toDefaultString()}>`;
    }
    isEmpty() {
        return this.#data == null || Object.keys(this.#data).length === 0;
    }
    merge(utxoCol) {
        let currentUtxos = this;
        for (const address of utxoCol.addresses) {
            currentUtxos = currentUtxos.setAddressUtxos(address, utxoCol.getAddressUtxos(address));
        }
        return currentUtxos;
    }
    removeAddressUtxos(address) {
        const newData = { ...this.#data };
        delete newData[address.toString()];
        const col = new UtxoCollection({ ...this });
        col.#data = Object.freeze(newData);
        return col;
    }
    select(amount, feeEstimator) {
        const emptyUtxoCol = UtxoCollection.createEmpty({ currency: this.currency });
        if (amount.gt(this.value))
            return [emptyUtxoCol, this.clone()];
        const selectFromGroup = (utxos, minimumAmount) => {
            let selected = emptyUtxoCol;
            let remaining = emptyUtxoCol;
            if (utxos.size === 0)
                return [selected, remaining];
            const arrUtxos = minimumAmount.defaultUnit.unitName === 'BTC'
                ? sortBy(utxos.toArray(), (utxo) => !utxo.confirmations, (utxo) => utxo.value.toDefaultNumber())
                : sortBy(utxos.toArray(), (utxo) => utxo.value.toDefaultNumber());
            const selectedUtxos = [];
            const currency = arrUtxos[0].value.unitType;
            let sum = currency.ZERO;
            for (const utxo of arrUtxos) {
                selectedUtxos.push(utxo);
                sum = sum.add(utxo.value);
                if (sum.gte(minimumAmount)) {
                    selected = UtxoCollection.fromArray(selectedUtxos, { ...this });
                    remaining = utxos.difference(selected);
                    return [selected, remaining];
                }
            }
            selected = UtxoCollection.fromArray(selectedUtxos, { ...this });
            remaining = utxos.difference(selected);
            return [selected, remaining];
        };
        let [selected, remaining] = selectFromGroup(this, amount);
        const fee = feeEstimator({ inputs: selected });
        const total = amount.add(fee);
        if (total.gt(selected.value)) {
            if (total.lte(this.value)) {
                const newFee = feeEstimator({ inputs: remaining });
                const newTotal = total.add(newFee);
                if (newTotal.gte(this.value))
                    return [this, emptyUtxoCol];
                const [feeSelected, feeRemaining] = selectFromGroup(remaining, fee.add(newFee));
                remaining = feeRemaining;
                selected = selected.union(feeSelected);
            }
            else {
                return [emptyUtxoCol, this];
            }
        }
        return [selected, remaining];
    }
    updateConfirmations(txs) {
        return UtxoCollection.fromArray(this.toArray().map((utxo) => {
            const tx = txs.find((tx) => tx.txId === utxo.txId);
            if (tx)
                utxo.confirmations = tx.confirmations;
            return utxo;
        }), { ...this });
    }
    addUtxo(utxo, addressMap) {
        return UtxoCollection.fromArray([...this.toArray(), utxo], { ...this, addressMap });
    }
    setAddressUtxos(address, utxos, { addressMap } = Object.create(null)) {
        if (Array.isArray(utxos)) {
            if (utxos.length === 0)
                return this.removeAddressUtxos(address);
            utxos = UtxoCollection.fromArray(utxos, { ...this, addressMap });
        }
        assert(utxos instanceof UtxoCollection, 'utxos must be an Array or UtxoCollection');
        if (utxos.size === 0)
            return this.removeAddressUtxos(address);
        const newData = { ...this.#data, [address.toString()]: utxos.toArray() };
        const col = new UtxoCollection({ ...this });
        col.#data = Object.freeze(newData);
        return col;
    }
    toArray() {
        return Object.values(this.#data).flat();
    }
    toPriorityOrderedArray(descending = false) {
        return sortBy(this.toArray(), (utxo) => !utxo.confirmations && utxo.rbfEnabled, (utxo) => !utxo.confirmations, (utxo) => utxo.value.toDefaultNumber() * (descending ? -1 : 1));
    }
    toJSON() {
        const jsonMap = Object.create(null);
        [...this.addresses].forEach((addr) => {
            const address = addr.toString();
            const sortedUtxos = sortBy(this.#data[address], (utxo) => utxo.value.toDefaultNumber());
            jsonMap[address] = {
                address,
                path: addr.meta.path,
                utxos: sortedUtxos.map(({ value, address: _address, ...rest }) => {
                    return { ...rest, value: value.toDefaultString({ unit: true }) };
                }),
            };
        });
        return jsonMap;
    }
    union(utxos) {
        const arr1 = this.toArray();
        const arr2 = utxos.toArray();
        return UtxoCollection.fromArray([...arr1, ...arr2], { ...this });
    }
    filter(predicate) {
        return UtxoCollection.fromArray(this.toArray().filter(predicate), { ...this });
    }
    unionOverwrite(utxos) {
        const arr1 = this.toArray();
        const arr2 = utxos.toArray();
        const prunedArr1 = differenceBy(arr1, arr2, (utxo) => `${utxo.txId}:${utxo.vout}`);
        const utxoArray = [...prunedArr1, ...arr2];
        return UtxoCollection.fromArray(utxoArray, { ...this });
    }
    get addresses() {
        if (this.#addresses)
            return this.#addresses;
        const addrs = [];
        Object.entries(this.#data).forEach(([addr, utxos]) => {
            if (!Array.isArray(utxos))
                return;
            if (utxos.length === 0)
                return;
            addrs.push(utxos[0].address || Address.create(addr));
        });
        if (addrs.length === 0) {
            this.#addresses = AddressSet.EMPTY;
            return this.#addresses;
        }
        addrs.sort(AddressSet.PATH_SORTER);
        this.#addresses = AddressSet.fromArray(addrs);
        return this.#addresses;
    }
    get empty() {
        return this.#data == null || Object.keys(this.#data).length === 0;
    }
    get size() {
        if (typeof this.#size === 'number')
            return this.#size;
        this.#size = this.toArray().length;
        return this.#size;
    }
    get txIds() {
        return uniq(this.toArray().map((utxo) => utxo.txId));
    }
    get value() {
        if (this.#value)
            return this.#value;
        this.#value = this.toArray().reduce((sum, utxo) => sum.add(utxo.value), this.currency.ZERO);
        return this.#value;
    }
    *[Symbol.iterator]() {
        const arr = this.toArray();
        for (const val of arr)
            yield val;
    }
}
